import { useEffect } from 'react';
import { Field } from 'react-final-form';
import { Box, Checkbox, Flex, Grid, Label, Paragraph } from 'theme-ui';

import { ErrorLabel } from '@/components/Collection/Error';
import CreativeImages, {
  CreativeImagesProps,
} from '@/components/Collection/CreativeImages';

const requiredWarehouses = (value: string[]) =>
  value?.length > 0 ? undefined : 'At least one warehouse is required';

export default function CreativeAsset({
  title,
  images,
  disabled,
  warehouses,
  pendingFiles,
  warehouseIds,
  onFileSelect,
  onImageChange,
  fieldPrefix = '',
  itemWarehouseCounts,
  onWarehouseIdsChange,
}: CreativeImagesProps & {
  fieldPrefix?: string;
  warehouseIds: string[];
  itemWarehouseCounts?: Map<string, number>;
  warehouses: { id: string; name: string }[];
  onWarehouseIdsChange: (warehouseIds: string[]) => void;
}) {
  const isWarehouseSelectable = (warehouseName: string) => {
    if (!itemWarehouseCounts?.size) {
      return true;
    }
    return (itemWarehouseCounts.get(warehouseName) ?? 0) > 0;
  };
  const selectableWarehouseIds = warehouses
    .filter((warehouse) => isWarehouseSelectable(warehouse.name))
    .map((warehouse) => warehouse.id);
  useEffect(() => {
    if (!itemWarehouseCounts?.size || warehouseIds.length === 0) {
      return;
    }
    const nextWarehouseIds = warehouseIds.filter((warehouseId) =>
      selectableWarehouseIds.includes(warehouseId),
    );
    if (nextWarehouseIds.length !== warehouseIds.length) {
      onWarehouseIdsChange(nextWarehouseIds);
    }
  }, [
    itemWarehouseCounts,
    onWarehouseIdsChange,
    selectableWarehouseIds,
    warehouseIds,
  ]);

  const handleToggle = (warehouseId: string) => {
    const warehouse = warehouses.find((entry) => entry.id === warehouseId);
    if (warehouse && !isWarehouseSelectable(warehouse.name)) {
      return;
    }
    const next = warehouseIds.includes(warehouseId)
      ? warehouseIds.filter((wId) => wId !== warehouseId)
      : [...warehouseIds, warehouseId];
    onWarehouseIdsChange(next);
  };

  const handleSelectAll = () => {
    if (
      selectableWarehouseIds.length > 0 &&
      selectableWarehouseIds.every((warehouseId) =>
        warehouseIds.includes(warehouseId),
      )
    ) {
      onWarehouseIdsChange([]);
    } else {
      onWarehouseIdsChange(selectableWarehouseIds);
    }
  };

  return (
    <Flex
      sx={{
        border: '1px solid',
        flexDirection: 'column',
        '@media (min-width: 1000px)': {
          flexDirection: 'row',
        },
        borderColor: 'secondaryHighlight',
      }}
    >
      <Field name={`${fieldPrefix}warehouseIds`} validate={requiredWarehouses}>
        {({ meta }) => {
          const error = (meta.touched && meta.error) || meta.submitError;
          return (
            <Flex
              sx={{
                gap: 3,
                flex: 1,
                padding: 4,
                flexDirection: 'column',
                borderRight: '1px solid',
                backgroundColor: '#F9F9F7',
                '@media (max-width: 1000px)': {
                  borderRight: 'none',
                },
                borderColor: 'secondaryHighlight',
              }}
            >
              <Paragraph sx={{ fontWeight: 'bold' }}>
                Will be shown in:
              </Paragraph>
              <Box
                sx={{ height: '1px', backgroundColor: 'secondaryHighlight' }}
              />
              <Paragraph
                onClick={disabled ? undefined : handleSelectAll}
                sx={{
                  fontSize: 1,
                  color: 'blue',
                  opacity: disabled ? 0.5 : 1,
                  cursor: disabled ? 'default' : 'pointer',
                }}
              >
                {selectableWarehouseIds.length > 0 &&
                selectableWarehouseIds.every((warehouseId) =>
                  warehouseIds.includes(warehouseId),
                )
                  ? 'Deselect All'
                  : 'Select All'}
              </Paragraph>
              <Grid
                sx={{
                  gap: 2,
                  gridTemplateColumns: [
                    '1fr',
                    'repeat(2, 1fr)',
                    'repeat(3, 1fr)',
                  ],
                }}
              >
                {warehouses.map((warehouse) => {
                  const count = itemWarehouseCounts?.get(warehouse.name) ?? 0;
                  const isSelectable = isWarehouseSelectable(warehouse.name);
                  return (
                    <Label
                      key={warehouse.id}
                      sx={{
                        display: 'flex',
                        overflow: 'hidden',
                        position: 'relative',
                        alignItems: 'center',
                        flexDirection: 'row',
                        opacity: isSelectable ? 1 : 0.5,
                        cursor:
                          disabled || !isSelectable ? 'default' : 'pointer',
                      }}
                    >
                      <Checkbox
                        disabled={disabled || !isSelectable}
                        onChange={() => handleToggle(warehouse.id)}
                        checked={warehouseIds.includes(warehouse.id)}
                      />
                      {`${warehouse.name}${
                        count > 0
                          ? ` (${count} ${count === 1 ? 'Item' : 'Items'})`
                          : ''
                      }`}
                    </Label>
                  );
                })}
              </Grid>
              <ErrorLabel message={error} />
            </Flex>
          );
        }}
      </Field>
      <CreativeImages
        title={title}
        images={images}
        disabled={disabled}
        pendingFiles={pendingFiles}
        onFileSelect={onFileSelect}
        onImageChange={onImageChange}
      />
    </Flex>
  );
}
